import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getReservationAiWsUrl,
  getReservationCustomerIdentity,
  ReservationAiClient,
  type ReservationAiSendPayload,
  type ReservationAiStreamMessage,
} from '../services/reservationAiClient';
import { getUserTimeZone } from '../utils/reservationDateTime';
import {
  type AiCaptainChoicePrompt,
  normalizeAiCaptainChoicePrompt,
} from '../utils/aiCaptainChoices';

export interface ReservationChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  choicePrompt?: AiCaptainChoicePrompt;
  choiceAnswered?: boolean;
  confirmedReservation?: Record<string, unknown>;
}

export interface UseReservationAiSessionOptions {
  enabled: boolean;
  appName: string | null;
  restaurantId: string;
  restaurantName: string;
  userId: number | null;
  guestName?: string;
  guestPhone?: string;
  locations?: { id: number; name: string }[];
}

export interface ConfirmedReservation {
  id?: number;
  fullName?: string;
  phoneNumber?: string;
  totalSeats?: number;
  occassion?: string;
  bookingDateTime?: string;
}

export default function useReservationAiSession(options: UseReservationAiSessionOptions) {
  const { enabled, appName, restaurantId, restaurantName, userId, guestName, guestPhone, locations } =
    options;

  const [connected, setConnected] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);
  const [messages, setMessages] = useState<ReservationChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [confirmed, setConfirmed] = useState<ConfirmedReservation | null>(null);

  const clientRef = useRef<ReservationAiClient | null>(null);
  const connectedRef = useRef(false);
  const startedRef = useRef(false);
  const contextRef = useRef({
    appName,
    restaurantId,
    restaurantName,
    userId,
    guestName,
    guestPhone,
    locations,
  });

  useEffect(() => {
    contextRef.current = {
      appName,
      restaurantId,
      restaurantName,
      userId,
      guestName,
      guestPhone,
      locations,
    };
  }, [appName, restaurantId, restaurantName, userId, guestName, guestPhone, locations]);

  const buildPayload = useCallback(
    (type: 'start' | 'user_message', text?: string): ReservationAiSendPayload | null => {
      const ctx = contextRef.current;
      if (!ctx.appName || !ctx.restaurantId) return null;
      const identity = getReservationCustomerIdentity(ctx.userId);
      return {
        type,
        restaurantId: ctx.restaurantId,
        appName: ctx.appName,
        customerId: identity.customerId,
        userId: identity.userId,
        text,
        context: {
          restaurantName: ctx.restaurantName,
          guestName: ctx.guestName,
          guestPhone: ctx.guestPhone,
          locations: ctx.locations,
          timeZone: getUserTimeZone(),
        },
      };
    },
    []
  );

  useEffect(() => {
    if (!enabled || !appName || !restaurantId) {
      clientRef.current?.disconnect();
      clientRef.current = null;
      connectedRef.current = false;
      startedRef.current = false;
      setConnected(false);
      setConnectionFailed(false);
      return;
    }

    setConnectionFailed(false);
    const failTimer = window.setTimeout(() => {
      if (!connectedRef.current) {
        setConnectionFailed(true);
      }
    }, 8000);

    const client = new ReservationAiClient({
      wsUrl: getReservationAiWsUrl(),
      onMessage: (msg: ReservationAiStreamMessage) => {
        switch (msg.type) {
          case 'assistant_message': {
            setStreaming(false);
            const choice = msg.choice ? normalizeAiCaptainChoicePrompt(msg.choice) : null;
            const displayText = msg.text?.trim() || choice?.prompt || '';
            if (!displayText && !choice) break;
            setMessages((prev) => [
              ...prev,
              {
                id: choice ? `choice-${choice.promptId}` : `ai-${Date.now()}`,
                role: 'assistant',
                text: displayText,
                choicePrompt: choice ?? undefined,
              },
            ]);
            break;
          }
          case 'reservation_confirmed': {
            setStreaming(false);
            const reservation = msg.reservation ?? {};
            setConfirmed({
              id: reservation.id != null ? Number(reservation.id) : undefined,
              fullName: reservation.fullName ? String(reservation.fullName) : undefined,
              phoneNumber: reservation.phoneNumber ? String(reservation.phoneNumber) : undefined,
              totalSeats:
                reservation.totalSeats != null ? Number(reservation.totalSeats) : undefined,
              occassion: reservation.occassion ? String(reservation.occassion) : undefined,
              bookingDateTime: reservation.bookingDateTime
                ? String(reservation.bookingDateTime)
                : undefined,
            });
            const confirmText =
              'Your table is reserved! We will notify you once the restaurant confirms.';
            setMessages((prev) => [
              ...prev,
              {
                id: `confirmed-${Date.now()}`,
                role: 'assistant',
                text: confirmText,
                confirmedReservation: reservation,
              },
            ]);
            break;
          }
          case 'error':
            setStreaming(false);
            if (msg.message) {
              setMessages((prev) => [
                ...prev,
                { id: `err-${Date.now()}`, role: 'assistant', text: msg.message! },
              ]);
            }
            break;
          case 'done':
            setStreaming(false);
            break;
          default:
            break;
        }
      },
      onConnectionChange: (isConnected) => {
        connectedRef.current = isConnected;
        setConnected(isConnected);
        if (isConnected) {
          setConnectionFailed(false);
          if (!startedRef.current) {
            startedRef.current = true;
            const payload = buildPayload('start');
            if (payload) {
              setStreaming(true);
              clientRef.current?.send(payload);
            }
          }
        }
      },
    });

    client.connect();
    clientRef.current = client;

    return () => {
      window.clearTimeout(failTimer);
      client.disconnect();
      clientRef.current = null;
      startedRef.current = false;
    };
  }, [enabled, appName, restaurantId, buildPayload]);

  const sendUserMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const payload = buildPayload('user_message', trimmed);
      if (!payload) return;
      setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', text: trimmed }]);
      setStreaming(true);
      clientRef.current?.send(payload);
    },
    [buildPayload]
  );

  const submitChoiceSelection = useCallback(
    (messageId: string, values: string[]) => {
      const text = values.map((v) => v.trim()).filter(Boolean).join(', ');
      if (!text) return;
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, choiceAnswered: true } : m))
      );
      sendUserMessage(text);
    },
    [sendUserMessage]
  );

  return {
    connected,
    connectionFailed,
    messages,
    streaming,
    confirmed,
    sendUserMessage,
    submitChoiceSelection,
  };
}
